import { useEffect, useRef, useState } from "react";
import { Mic, Video as VideoIcon, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";

/**
 * Pre-call device check. Runs a live mic + camera preview before allowing
 * the user to enter the video room. Ensures teens/parents don't get dropped
 * into a black-screen room because the browser silently blocked permissions.
 */
export default function PreCallCheck({ onReady, participantLabel }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | requesting | ok | denied | error
  const [error, setError] = useState("");
  const [audioLevel, setAudioLevel] = useState(0);
  const [hasCam, setHasCam] = useState(false);
  const [hasMic, setHasMic] = useState(false);

  useEffect(() => {
    let audioCtx = null;
    let raf = null;
    let cancelled = false;

    async function start() {
      setStatus("requesting");
      setError("");
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 } },
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        setHasCam(stream.getVideoTracks().length > 0);
        setHasMic(stream.getAudioTracks().length > 0);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try { await videoRef.current.play(); } catch (_) { /* autoplay-block ok */ }
        }
        // Mic level meter (Web Audio)
        try {
          audioCtx = new (window.AudioContext || window.webkitAudioContext)();
          const source = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 512;
          source.connect(analyser);
          const buf = new Uint8Array(analyser.frequencyBinCount);
          const tick = () => {
            if (cancelled) return;
            analyser.getByteTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) {
              const v = (buf[i] - 128) / 128;
              sum += v * v;
            }
            const rms = Math.sqrt(sum / buf.length);
            setAudioLevel(Math.min(100, Math.round(rms * 220)));
            raf = requestAnimationFrame(tick);
          };
          tick();
        } catch (_) { /* audio meter is optional */ }
        setStatus("ok");
      } catch (e) {
        if (cancelled) return;
        const name = e?.name || "";
        if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          setStatus("denied");
          setError("Camera or microphone permission was blocked. Please allow both in your browser and try again.");
        } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
          setStatus("error");
          setError("No camera or microphone was found on this device.");
        } else {
          setStatus("error");
          setError(e?.message || "Could not access camera/microphone.");
        }
      }
    }
    start();

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (audioCtx && audioCtx.state !== "closed") { try { audioCtx.close(); } catch (_) {} }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const handleReady = () => {
    // Release preview stream so Jitsi can grab the devices cleanly.
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    onReady?.();
  };

  return (
    <div className="card-soft p-6" data-testid="precall-check">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <span className="label-eyebrow">Before you join</span>
          <h2 className="font-serif-display text-2xl mt-1">Let's check your camera and mic</h2>
          {participantLabel && (
            <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
              You'll be joining as <strong>{participantLabel}</strong>
            </div>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Video preview */}
        <div className="relative rounded-lg overflow-hidden bg-black" style={{ aspectRatio: "4/3" }} data-testid="precall-video-preview">
          {status === "requesting" && (
            <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Requesting camera & mic…
            </div>
          )}
          {(status === "denied" || status === "error") && (
            <div className="absolute inset-0 flex items-center justify-center text-white/90 text-sm p-4 text-center">
              <div>
                <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                {error}
              </div>
            </div>
          )}
          <video ref={videoRef} muted playsInline autoPlay
                 className="w-full h-full object-cover"
                 style={{ display: status === "ok" ? "block" : "none" }} />
        </div>

        {/* Checklist + mic meter */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <VideoIcon className="w-5 h-5" style={{ color: "var(--brand)" }} />
            <div className="flex-1">
              <div className="text-sm font-semibold">Camera</div>
              <div className="text-xs" style={{ color: "var(--text-secondary)" }} data-testid="precall-cam-status">
                {status === "ok" && hasCam ? "Working ✓" : status === "requesting" ? "Checking…" : status === "denied" ? "Blocked" : "Not detected"}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Mic className="w-5 h-5" style={{ color: "var(--brand)" }} />
            <div className="flex-1">
              <div className="text-sm font-semibold">Microphone</div>
              <div className="text-xs" style={{ color: "var(--text-secondary)" }} data-testid="precall-mic-status">
                {status === "ok" && hasMic ? "Working ✓ · say hi to test the meter below" : status === "requesting" ? "Checking…" : status === "denied" ? "Blocked" : "Not detected"}
              </div>
              {status === "ok" && (
                <div className="mt-2 h-2 w-full rounded-full bg-gray-200 overflow-hidden" data-testid="precall-mic-meter">
                  <div className="h-full transition-[width] duration-100"
                       style={{ width: `${audioLevel}%`, background: "var(--brand)" }} />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-lg p-3"
               style={{ background: "rgba(91,117,83,0.08)" }}>
            <ShieldCheck className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: "var(--brand)" }} />
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
              <div className="font-semibold text-sm mb-0.5" style={{ color: "var(--text-primary)" }}>
                Private 1-to-1 session
              </div>
              This room is not recorded. Only you and your counselor can join. Please find a quiet, private space before you start.
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={handleReady}
              disabled={status !== "ok"}
              className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="precall-continue-btn">
              I'm ready — join session
            </button>
            {(status === "denied" || status === "error") && (
              <button
                onClick={() => window.location.reload()}
                className="btn-outline text-sm"
                data-testid="precall-retry-btn">
                Retry
              </button>
            )}
          </div>
          {status === "denied" && (
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
              On the browser tab, click the padlock icon → Site settings → set Camera & Microphone to "Allow", then reload this page.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
